/* dashboard-view.jsx — Workspace Dashboard */
(function () {
  const { useState, useMemo, useEffect } = React;
  const NexusIcon = window.NexusIcon;
  const Pill      = window.Pill;
  const Btn       = window.Btn;
  const IconBtn   = window.IconBtn;

  const DASH_RANGES = [
    { id:'today',     label:'Today'           },
    { id:'yesterday', label:'Yesterday'       },
    { id:'7',         label:'Last 7 Days'     },
    { id:'30',        label:'Last 30 Days'    },
    { id:'90',        label:'Last 90 Days'    },
    { id:'thismonth', label:'This Month'      },
    { id:'lastmonth', label:'Last Month'      },
    { id:'qtd',       label:'Quarter To Date' },
    { id:'ytd',       label:'Year To Date'    },
    { id:'custom',    label:'Custom Range'    },
  ];

  /* Canonical tone tokens for dashboard semantic coloring (DS-05). */
  const D_TONES = {
    primary: 'var(--nx-tone-primary)',
    success: 'var(--nx-tone-success)',
    purple:  'var(--nx-tone-purple)',
    amber:   'var(--nx-tone-amber)',
    rose:    'var(--nx-tone-rose)',
    danger:  'var(--nx-danger)',
  };

  /* Returns { from: Date|null, to: Date|null } for each range preset or custom dates. */
  const getRangeBounds = (rangeId, customStart, customEnd) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()); // local midnight
    if (rangeId === 'today')     { return { from: today, to: new Date(today.getTime() + 86400000 - 1) }; }
    if (rangeId === 'yesterday') { const ys = new Date(today); ys.setDate(today.getDate() - 1); return { from: ys, to: new Date(today.getTime() - 1) }; }
    if (rangeId === '7')         { const d = new Date(today); d.setDate(d.getDate() - 7);  return { from: d, to: null }; }
    if (rangeId === '30')        { const d = new Date(today); d.setDate(d.getDate() - 30); return { from: d, to: null }; }
    if (rangeId === '90')        { const d = new Date(today); d.setDate(d.getDate() - 90); return { from: d, to: null }; }
    if (rangeId === 'thismonth') { return { from: new Date(today.getFullYear(), today.getMonth(), 1), to: null }; }
    if (rangeId === 'lastmonth') { return { from: new Date(today.getFullYear(), today.getMonth() - 1, 1), to: new Date(today.getFullYear(), today.getMonth(), 0, 23, 59, 59, 999) }; }
    if (rangeId === 'qtd')       { return { from: new Date(today.getFullYear(), Math.floor(today.getMonth()/3)*3, 1), to: null }; }
    if (rangeId === 'ytd')       { return { from: new Date(today.getFullYear(), 0, 1), to: null }; }
    if (rangeId === 'custom' && customStart) {
      const [cy,cm,cd] = customStart.split('-').map(Number);
      const from = new Date(cy, cm-1, cd);
      const to   = customEnd ? (() => { const [ey,em,ed] = customEnd.split('-').map(Number); return new Date(ey, em-1, ed, 23, 59, 59, 999); })() : null;
      return { from, to };
    }
    return { from: null, to: null };
  };

  /* Timezone-safe date range check. Date-only strings (YYYY-MM-DD) parsed as local midnight. */
  const parseLocalDate = (dateStr) => {
    if (!dateStr) return null;
    const s = String(dateStr);
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) { const [y,m,d] = s.split('-').map(Number); return new Date(y, m-1, d); }
    const dt = new Date(s); return isNaN(dt.getTime()) ? null : dt;
  };
  const inRange = (dateStr, from, to) => {
    if (!from) return true;
    const dt = parseLocalDate(dateStr);
    if (!dt) return false;
    if (dt < from) return false;
    if (to && dt > to) return false;
    return true;
  };

  const RANGE_TO_TREND = { today:'7D', yesterday:'7D', '7':'7D', '30':'30D', '90':'3M', thismonth:'30D', lastmonth:'30D', qtd:'3M', ytd:'12M', custom:'30D' };

  /* Derive won/lost stage IDs from stage names — backend uses UUIDs, seed uses s5/s6 */
  const getTerminalIds = (stages) => ({
    wonId:  stages.find(s => s.kind === 'WON')?.id  || stages.find(s => s.name?.toLowerCase() === 'won')?.id  || null,
    lostId: stages.find(s => s.kind === 'LOST')?.id || stages.find(s => s.name?.toLowerCase() === 'lost')?.id || null,
  });

  /* Build cumulative trend series from real deal createdAt dates */
  const buildTrendPts = (allDeals, ptRange, ptMode, wonId, lostId) => {
    const now = new Date();
    const startOf = {
      '7D':  () => { const d = new Date(now); d.setDate(d.getDate()-7); return d; },
      '30D': () => { const d = new Date(now); d.setDate(d.getDate()-30); return d; },
      '3M':  () => { const d = new Date(now); d.setMonth(d.getMonth()-3); return d; },
      '6M':  () => { const d = new Date(now); d.setMonth(d.getMonth()-6); return d; },
      '12M': () => { const d = new Date(now); d.setFullYear(d.getFullYear()-1); return d; },
    };
    const start = (startOf[ptRange] || startOf['30D'])();
    const totalMs = now - start;
    const N = { '7D':7,'30D':5,'3M':6,'6M':6,'12M':6 }[ptRange] || 6;
    const activeDls = allDeals.filter(d => d.stageId !== wonId && d.stageId !== lostId && d.createdAt);
    const series = Array.from({length: N}, (_, i) => {
      const bEnd = new Date(start.getTime() + ((i+1)/N)*totalMs);
      const cumulative = activeDls.filter(d => new Date(d.createdAt) <= bEnd);
      return ptMode === 'value' ? cumulative.reduce((s,d)=>s+d.value,0) : cumulative.length;
    });
    const labels = Array.from({length: N}, (_, i) => {
      if (i === N-1) return 'Now';
      const t = new Date(start.getTime() + ((i+0.5)/N)*totalMs);
      if (ptRange === '7D') return ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][t.getDay()].slice(0,3);
      if (ptRange === '30D') return t.toLocaleDateString('en-US', {month:'short', day:'numeric'});
      return t.toLocaleDateString('en-US', {month:'short'});
    });
    return { series, labels };
  };

  const Skel = ({ w, h, r=4, style={} }) => (
    <span className="nx-skel" style={{ display:'block', width:w||'100%', height:h||12, borderRadius:r, ...style }} />
  );

  /* Reusable panel-body skeleton — used by widgets that have their own header */
  const BodySkel = ({ rows=4, heights }) => (
    <div className="nx-panel-body" style={{ display:'flex', flexDirection:'column', gap:10, minHeight:80 }}>
      {Array.from({length:rows},(_,i)=>{
        const h = heights ? heights[i%heights.length] : 13;
        const w = i%3===0?'80%':i%3===1?'60%':'70%';
        return <Skel key={i} h={h} w={w} />;
      })}
    </div>
  );

  /* ─── DealRow ─────────────────────────────────────────── */
  const DealRow = ({ deal:d, stages, companies, fmt, Pill, NexusIcon, IconBtn, OwnerAv, toast, onOpenRecord }) => {
    const [mOpen, setMOpen] = useState(false);
    const { lostId } = getTerminalIds(stages);
    const stage    = stages.find(s => s.id === d.stageId) || stages[0];
    const stageIdx = stages.findIndex(s => s.id === d.stageId);
    const totalSt  = stages.filter(s => s.id !== lostId).length;
    const prob     = Math.round(((stageIdx+1)/totalSt)*100);
    const acct     = companies.find(c => c.id === d.companyId);
    const initials = (d.owner||'').split(' ').map(w=>w[0]).slice(0,2).join('');
    return (
      <tr className="nx-tr" style={{ cursor:'pointer' }} onClick={() => onOpenRecord(d)}>
        <td style={{ maxWidth:180 }}>
          <span title={d.title} style={{ display:'block', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', fontWeight:600, color:'var(--nx-accent)', fontSize:13 }}>{d.title}</span>
        </td>
        <td><span className="nx-muted" style={{ whiteSpace:'nowrap' }}>{acct?.name||'—'}</span></td>
        <td><Pill tone={['negotiation','proposal sent'].includes(stage.name?.toLowerCase())?'amber':stage.name?.toLowerCase()==='qualified'?'green':'blue'}>{stage.name}</Pill></td>
        <td><span className="nx-cell is-mono" style={{ fontWeight:700 }}>{fmt(d.value)}</span></td>
        <td><span className="nx-muted" style={{ whiteSpace:'nowrap', fontSize:12 }}>{d.closeDate}</span></td>
        <td style={{ minWidth:110 }}>
          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
            <div style={{ flex:1, height:4, background:'var(--nx-divider)', borderRadius:3, overflow:'hidden' }}>
              <div style={{ width:prob+'%', height:'100%', background:prob>=70?'var(--nx-success)':'var(--nx-accent)', borderRadius:3 }} />
            </div>
            <span style={{ fontSize:11, fontWeight:700, minWidth:28, textAlign:'right', color:'var(--nx-text)' }}>{prob}%</span>
          </div>
        </td>
        <td>
          {OwnerAv ? <span style={{ display:'flex', alignItems:'center', gap:5 }}><OwnerAv name={d.owner||'?'} size={20} /><span style={{ fontSize:12, fontWeight:600, color:'var(--nx-text-muted)' }}>{initials}</span></span>
            : <span style={{ fontSize:12 }}>{initials}</span>}
        </td>
        <td className="nx-td-end" onClick={e=>e.stopPropagation()}>
          <div style={{ position:'relative' }}>
            <IconBtn icon="MoreVertical" size={13} onClick={() => setMOpen(o=>!o)} />
            {mOpen && (
              <div className="nx-chip-menu" style={{ right:0, left:'auto', top:24, zIndex:40 }} onMouseLeave={() => setMOpen(false)}>
                <button className="nx-chip-menu-item" onClick={() => { onOpenRecord(d); setMOpen(false); }}><NexusIcon name="ArrowRight" size={11} /><span>Open Deal</span></button>
                <button className="nx-chip-menu-item" onClick={() => { toast({tone:'info',icon:'Edit',title:'Edit Deal'}); setMOpen(false); }}><NexusIcon name="Edit" size={11} /><span>Edit</span></button>
              </div>
            )}
          </div>
        </td>
      </tr>
    );
  };

  /* ─── ExportDropdown ─────────────────────────────────── */
  const ExportDropdown = ({ onCSV, onExcel, onPDF }) => {
    const [open, setOpen] = useState(false);
    const ref = React.useRef(null);
    useEffect(() => {
      if (!open) return;
      const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
      document.addEventListener('mousedown', h);
      return () => document.removeEventListener('mousedown', h);
    }, [open]);
    return (
      <div ref={ref} style={{ position:'relative', display:'inline-flex' }}>
        <button className="nx-btn nx-btn-secondary nx-btn-sm" onClick={() => setOpen(o=>!o)}>
          <NexusIcon name="Download" size={13} /><span>Export</span>
          <NexusIcon name="ChevronDown" size={11} style={{ marginLeft:2, transition:'transform .15s', transform:open?'rotate(180deg)':'rotate(0deg)' }} />
        </button>
        {open && (
          <div className="nx-export-menu" role="menu">
            {[{label:'CSV',icon:'FileText',action:onCSV},{label:'Excel',icon:'FileSpreadsheet',action:onExcel},{label:'PDF',icon:'Printer',action:onPDF}].map(it => (
              <button key={it.label} className="nx-export-menu-item" role="menuitem" onClick={() => { setOpen(false); it.action(); }}>
                <NexusIcon name={it.icon} size={13} /><span>{it.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════
     SECTION 1 — SMART KPI CARD (compact + sparkline)
     ═══════════════════════════════════════════════════════ */
  const SmartKpiCard = ({ label, value, trend, up, sub, icon, color, series, onClick, loading }) => {
    const W=90, H=28, pad=2;
    let linePath=null, areaPath=null;
    if (series && series.length > 1) {
      const mn=Math.min(...series), mx=Math.max(...series), rng=mx-mn||1;
      const pts = series.map((v,i) => [pad+(i/(series.length-1))*(W-pad*2), H-pad-((v-mn)/rng)*(H-pad*2)]);
      linePath = pts.map((p,i)=>(i===0?'M':'L')+p[0].toFixed(1)+' '+p[1].toFixed(1)).join(' ');
      areaPath = linePath+` L ${pts[pts.length-1][0].toFixed(1)} ${H} L ${pts[0][0].toFixed(1)} ${H} Z`;
    }
    const gId = 'ksg_'+label.replace(/[^a-zA-Z0-9]/g,'_');
    if (loading) return (
      <div className="nx-kpi-compact">
        <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6 }}><Skel w={26} h={26} r={7} /><Skel w={40} h={16} r={4} /></div>
        <Skel w="55%" h={20} r={4} style={{ marginBottom:3 }} /><Skel w="70%" h={10} r={3} style={{ marginBottom:2 }} />
        <Skel w="50%" h={9} r={3} style={{ marginBottom:8 }} /><Skel w="100%" h={28} r={3} />
      </div>
    );
    return (
      <div className="nx-kpi-compact" style={{ '--kc':color, cursor:onClick?'pointer':'default' }} onClick={onClick}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:6 }}>
          <span style={{ width:26,height:26,borderRadius:7,display:'grid',placeItems:'center',background:`color-mix(in oklab,${color},white 84%)`,color,flexShrink:0 }}>
            <NexusIcon name={icon} size={13} />
          </span>
          {trend && (
            <span className={'nx-kpi-v2-trend '+(up?'is-up':'is-dn')}>
              <NexusIcon name={up?'TrendingUp':'TrendingDown'} size={9} />{trend}
            </span>
          )}
        </div>
        <div style={{ fontSize:20,fontWeight:900,color:'var(--nx-text)',letterSpacing:'-0.03em',lineHeight:1.1,fontVariantNumeric:'tabular-nums',marginBottom:2 }}>{value}</div>
        <div style={{ fontSize:11,fontWeight:600,color:'var(--nx-text)',marginBottom:1 }}>{label}</div>
        <div style={{ fontSize:10,color:'var(--nx-text-faint)',marginBottom:4 }}>{sub}</div>
        {linePath && (
          <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width:'100%',height:28,display:'block' }}>
            <defs><linearGradient id={gId} x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.22" />
              <stop offset="100%" stopColor={color} stopOpacity="0" />
            </linearGradient></defs>
            <path d={areaPath} fill={`url(#${gId})`} />
            <path d={linePath} stroke={color} strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        )}
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════
     SECTION 2 — PIPELINE BY STAGE (donut / bar toggle)
     ═══════════════════════════════════════════════════════ */
  const StageBarChart = ({ deals, stages, fmt, onNavigate, loading }) => {
    const [chartView, setChartView] = useState('donut');
    if (loading) return (
      <div className="nx-panel nx-stage-chart-panel">
        <div className="nx-panel-head"><Skel h={14} w={160} r={3} /><Skel h={22} w={80} r={4} /></div>
        <BodySkel rows={5} heights={[12,9,12,9,12]} />
      </div>
    );
    const { lostId } = getTerminalIds(stages);
    const activeStages = stages.filter(s => s.id !== lostId);
    const stageData = activeStages.map(s => ({
      ...s,
      cnt: deals.filter(d => d.stageId === s.id).length,
      val: deals.filter(d => d.stageId === s.id).reduce((sum,d) => sum+d.value, 0),
    }));
    const maxVal   = Math.max(...stageData.map(s=>s.val), 1);
    const totalVal = stageData.reduce((s,x) => s+x.val, 0);
    const R=44, circ=2*Math.PI*R;
    let cumLen=0;
    const donutSegs = stageData.map(s => {
      const len = totalVal>0 ? (s.val/totalVal)*circ : 0;
      const gap = stageData.length>1 ? 1.8 : 0;
      const seg = { ...s, dashArray:`${Math.max(len-gap,0)} ${circ}`, startLen:cumLen };
      cumLen += len; return seg;
    });
    const BW=340, BH=140, bp={l:38,r:8,t:22,b:30};
    const innerH=BH-bp.t-bp.b, innerW=BW-bp.l-bp.r;
    if (stages.length === 0) return (
      <div className="nx-panel nx-stage-chart-panel">
        <div className="nx-panel-head"><h3 className="nx-panel-title">Pipeline by Stage</h3></div>
        <div className="nx-empty" style={{ padding:'36px 16px' }}><NexusIcon name="BarChart2" size={20} /><span>No pipeline stages configured.</span></div>
      </div>
    );
    return (
      <div className="nx-panel nx-stage-chart-panel">
        <div className="nx-panel-head">
          <div>
            <h3 className="nx-panel-title">Pipeline by Stage</h3>
            <span className="nx-stage-chart-sub">{fmt(totalVal)} total pipeline</span>
          </div>
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <div className="nx-pt-toggle">
              <button className={'nx-pt-tog'+(chartView==='donut'?' is-on':'')} onClick={() => setChartView('donut')}>Donut</button>
              <button className={'nx-pt-tog'+(chartView==='bar'?' is-on':'')} onClick={() => setChartView('bar')}>Bar</button>
            </div>
            <button className="nx-panel-link" onClick={() => onNavigate('deals')}>View Pipeline <NexusIcon name="ArrowRight" size={11} /></button>
          </div>
        </div>
        {chartView==='donut' && (
          <div className="nx-panel-body nx-stage-donut-body">
            <div className="nx-stage-donut-chart">
              <svg viewBox="0 0 120 120" width="100%" height="100%">
                <circle cx="60" cy="60" r={R} fill="none" stroke="var(--nx-divider)" strokeWidth="16" />
                {donutSegs.map(seg => seg.val>0 && (
                  <circle key={seg.id} cx="60" cy="60" r={R} fill="none" stroke={seg.tone} strokeWidth="16"
                    strokeDasharray={seg.dashArray} strokeDashoffset={-seg.startLen+circ/4}
                    transform="rotate(-90 60 60)" style={{ transition:'stroke-dasharray .4s ease' }} />
                ))}
                <text x="60" y="54" textAnchor="middle" fontSize="12" fontWeight="800" fill="var(--nx-text)">{fmt(totalVal)}</text>
                <text x="60" y="66" textAnchor="middle" fontSize="7" fontWeight="600" fill="var(--nx-text-muted)">Total</text>
              </svg>
            </div>
            <div style={{ flex:1, minWidth:0, display:'flex', flexDirection:'column', gap:3 }}>
              {stageData.map(s => {
                const pct=totalVal>0?Math.round((s.val/totalVal)*100):0;
                const bW=Math.max(4,Math.round((s.val/maxVal)*100));
                return (
                  <div key={s.id} style={{ display:'grid', gridTemplateColumns:'1fr 54px 28px 20px', alignItems:'center', gap:6, padding:'5px 4px', borderRadius:5, cursor:'pointer', transition:'background .1s' }}
                    onClick={() => onNavigate('deals')}
                    onMouseEnter={e => e.currentTarget.style.background='var(--nx-surface-alt)'}
                    onMouseLeave={e => e.currentTarget.style.background=''}
                  >
                    <div style={{ display:'flex', alignItems:'center', gap:6, fontSize:12, fontWeight:600, color:'var(--nx-text)', minWidth:0 }}>
                      <span style={{ width:8,height:8,borderRadius:'50%',background:s.tone,flexShrink:0 }} />
                      <span style={{ overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap' }}>{s.name}</span>
                    </div>
                    <span style={{ fontSize:12,fontWeight:700,color:'var(--nx-text)',textAlign:'right',fontVariantNumeric:'tabular-nums' }}>{fmt(s.val)}</span>
                    <span style={{ fontSize:11,color:'var(--nx-text-muted)',textAlign:'right' }}>{pct}%</span>
                    <span style={{ fontSize:11,color:'var(--nx-text-faint)',textAlign:'right' }}>{s.cnt}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        {chartView==='bar' && (
          <div className="nx-panel-body" style={{ paddingTop: 10 }}>
            <svg viewBox={`0 0 ${BW} ${BH}`} style={{ width:'100%', height:BH, overflow:'visible' }}>
              {[0,0.25,0.5,0.75,1].map((f,i) => {
                const y=bp.t+innerH*(1-f), v=maxVal*f;
                const lbl=v>=1e6?(v/1e6).toFixed(1)+'M':v>=1e3?Math.round(v/1e3)+'K':Math.round(v);
                return (
                  <g key={i}>
                    <line x1={bp.l} y1={y} x2={BW-bp.r} y2={y} stroke="var(--nx-divider)" strokeWidth={i===0?1:0.5} strokeDasharray={i===0?'0':'3 2'} />
                    {i>0 && <text x={bp.l-4} y={y+3.5} textAnchor="end" fontSize="7" fill="var(--nx-text-muted)">{lbl}</text>}
                  </g>
                );
              })}
              {stageData.map((s,i) => {
                const slotW=innerW/stageData.length, bW=Math.max(slotW*0.58,14);
                const x=bp.l+i*slotW+(slotW-bW)/2, bH=Math.max(3,(s.val/maxVal)*innerH), y=bp.t+innerH-bH;
                const cx=bp.l+i*slotW+slotW/2;
                return (
                  <g key={s.id} style={{ cursor:'pointer' }} onClick={() => onNavigate('deals')}>
                    <rect x={x} y={y} width={bW} height={bH} fill={s.tone} rx="3" opacity="0.88"
                      onMouseEnter={e => e.currentTarget.setAttribute('opacity','1')}
                      onMouseLeave={e => e.currentTarget.setAttribute('opacity','0.88')} />
                    <text x={cx} y={y-4} textAnchor="middle" fontSize="7.5" fontWeight="700" fill="var(--nx-text)">{fmt(s.val)}</text>
                    <text x={cx} y={bp.t+innerH+14} textAnchor="middle" fontSize="7" fill="var(--nx-text-muted)">{s.name}</text>
                  </g>
                );
              })}
            </svg>
          </div>
        )}
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════
     SECTION 3 — PIPELINE TREND (area chart, unchanged)
     ═══════════════════════════════════════════════════════ */
  const PipelineTrend = ({ deals, allDeals, stages, fmt, onNavigate, loading }) => {
    const [ptMode, setPtMode]   = useState('value');
    const [ptRange, setPtRange] = useState('30D');
    if (loading) return (
      <div className="nx-panel nx-pt-panel">
        <div className="nx-panel-head"><Skel h={14} w={140} r={3} /><Skel h={22} w={140} r={4} /></div>
        <BodySkel rows={3} heights={[40,12,9]} />
      </div>
    );
    const { wonId, lostId }     = getTerminalIds(stages);
    const activeStages = stages.filter(s => s.id !== wonId && s.id !== lostId);
    const stageMetrics = activeStages.map(s => {
      const sd=deals.filter(d=>d.stageId===s.id);
      return { ...s, count:sd.length, value:sd.reduce((sum,d)=>sum+d.value,0) };
    });
    const totalVal=stageMetrics.reduce((s,x)=>s+x.value,0);
    const totalCnt=stageMetrics.reduce((s,x)=>s+x.count,0);
    const { series: basePts, labels: xLabels } = useMemo(
      () => buildTrendPts(allDeals || deals, ptRange, ptMode, wonId, lostId),
      [allDeals, deals, ptRange, ptMode, wonId, lostId]
    );
    const ptMax=Math.max(...basePts,1), W=320, H=52, pad=3;
    const pts = basePts.map((v,i)=>[pad+(i/(basePts.length-1))*(W-pad*2), H-pad-((v/ptMax)*(H-pad*2))]);
    const linePath=pts.map((p,i)=>(i===0?'M':'L')+p[0].toFixed(1)+' '+p[1].toFixed(1)).join(' ');
    const areaPath=linePath+` L ${pts[pts.length-1][0]} ${H} L ${pts[0][0]} ${H} Z`;
    return (
      <div className="nx-panel nx-home-pipe nx-pt-panel">
        <div className="nx-panel-head">
          <div><h3 className="nx-panel-title">Pipeline Trend</h3><span className="nx-pt-sub">Across all stages</span></div>
          <div className="nx-panel-actions" style={{ gap:6 }}>
            <div className="nx-pt-toggle">
              <button className={'nx-pt-tog'+(ptMode==='value'?' is-on':'')} onClick={() => setPtMode('value')}>Value</button>
              <button className={'nx-pt-tog'+(ptMode==='count'?' is-on':'')} onClick={() => setPtMode('count')}>Count</button>
            </div>
            <span className="nx-pt-ctrl-sep" />
            <div className="nx-pt-ranges">
              {['7D','30D','3M','6M','12M'].map(r=>(
                <button key={r} className={'nx-pt-range'+(ptRange===r?' is-on':'')} onClick={() => setPtRange(r)}>{r}</button>
              ))}
            </div>
          </div>
        </div>
        <div className="nx-pt-body">
          <div className="nx-pt-chart-wrap">
            <div className="nx-pt-chart-total">
              <span className="nx-pt-chart-total-val">{ptMode==='value'?fmt(totalVal):totalCnt}</span>
              <span className="nx-pt-chart-total-lbl">{ptMode==='value'?'Total Pipeline':'Total Opportunities'}</span>
            </div>
            <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="nx-pt-svg">
              <defs><linearGradient id="ptg4" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="var(--nx-accent)" stopOpacity="0.18" />
                <stop offset="100%" stopColor="var(--nx-accent)" stopOpacity="0" />
              </linearGradient></defs>
              <path d={areaPath} fill="url(#ptg4)" />
              <path d={linePath} stroke="var(--nx-accent)" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
              {pts.map((p,i)=>(
                <g key={i}>
                  <circle cx={p[0]} cy={p[1]} r={i===pts.length-1?3.5:2} fill="var(--nx-accent)" opacity={i===pts.length-1?1:0.4} />
                  {i===pts.length-1 && <text x={p[0]} y={p[1]-7} textAnchor="middle" fontSize="8" fill="var(--nx-accent)" fontWeight="700">{ptMode==='value'?fmt(basePts[i]):basePts[i]}</text>}
                </g>
              ))}
            </svg>
            <div className="nx-pt-xaxis">{xLabels.map((l,i)=><span key={i}>{l}</span>)}</div>
          </div>
          <div className="nx-pt-breakdown">
            <div className="nx-pt-bd-head"><span>Stage</span><span style={{ textAlign:'right' }}>{ptMode==='value'?'Value':'Count'}</span><span style={{ textAlign:'right' }}>%</span></div>
            {stageMetrics.map(s => {
              const val=ptMode==='value'?s.value:s.count, total=ptMode==='value'?totalVal:totalCnt;
              const pct=total?Math.round((val/total)*100):0;
              return (
                <div key={s.id} className="nx-pt-bd-row" onClick={() => onNavigate('deals')}>
                  <div className="nx-pt-bd-stage"><span className="nx-pt-bd-dot" style={{ background:s.tone }} /><span className="nx-pt-bd-name">{s.name}</span></div>
                  <span className="nx-pt-bd-val" style={{ textAlign:'right' }}>{ptMode==='value'?fmt(s.value):s.count}</span>
                  <span className="nx-pt-bd-pct">{pct}%</span>
                </div>
              );
            })}
            <div className="nx-pt-bd-total"><span>Total</span><span style={{ textAlign:'right' }}>{ptMode==='value'?fmt(totalVal):totalCnt}</span><span style={{ textAlign:'right' }}>100%</span></div>
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════
     SECTION 4 — WIN / LOSS INTELLIGENCE (horizontal stacked)
     ═══════════════════════════════════════════════════════ */
  const WinLossPanel = ({ wonDeals, lostDeals, activeDeals, winRate, fmt, wonValue, loading }) => {
    if (loading) return (
      <div className="nx-panel nx-wl-panel">
        <div className="nx-panel-head"><Skel h={14} w={120} r={3} /><Skel h={16} w={80} r={4} /></div>
        <BodySkel rows={4} heights={[8,13,13,13]} />
      </div>
    );
    const hasData = wonDeals.length + lostDeals.length + activeDeals.length > 0;
    const winColor = !hasData ? 'var(--nx-text-muted)' : winRate>=40?D_TONES.success:winRate>=20?D_TONES.amber:D_TONES.danger;
    const segs = [
      { label:'Won',    count:wonDeals.length,    color:D_TONES.success },
      { label:'Active', count:activeDeals.length, color:D_TONES.primary },
      { label:'Lost',   count:lostDeals.length,   color:D_TONES.danger  },
    ];
    const total = segs.reduce((s,x)=>s+x.count,0)||1;
    return (
      <div className="nx-panel nx-wl-panel">
        <div className="nx-panel-head">
          <h3 className="nx-panel-title">Win / Loss</h3>
          <span style={{ fontSize:11,fontWeight:800,color:winColor }}>{hasData ? winRate+'% Win Rate' : '—'}</span>
        </div>
        <div className="nx-panel-body" style={{ display:'flex',flexDirection:'column',gap:12 }}>
          <div style={{ height:8,borderRadius:4,overflow:'hidden',background:'var(--nx-divider)' }}>
            {hasData && segs.map(s=>s.count>0&&<div key={s.label} style={{ width:(s.count/total*100)+'%',height:'100%',background:s.color,float:'left',transition:'width .4s' }} />)}
          </div>
          <div style={{ display:'flex',flexDirection:'column',gap:9 }}>
            {segs.map(s=>(
              <div key={s.label} style={{ display:'flex',alignItems:'center',gap:9 }}>
                <span style={{ width:8,height:8,borderRadius:'50%',background:s.color,flexShrink:0 }} />
                <span style={{ flex:1,fontSize:12,fontWeight:600,color:'var(--nx-text)' }}>{s.label}</span>
                <div style={{ width:56,height:4,background:'var(--nx-divider)',borderRadius:3,overflow:'hidden' }}>
                  <div style={{ width:(s.count/total*100)+'%',height:'100%',background:s.color,borderRadius:3 }} />
                </div>
                <span style={{ fontSize:13,fontWeight:800,color:'var(--nx-text)',minWidth:18,textAlign:'right' }}>{s.count}</span>
                <span style={{ fontSize:10,color:'var(--nx-text-muted)',minWidth:34,textAlign:'right' }}>{Math.round(s.count/total*100)}%</span>
              </div>
            ))}
          </div>
          <div style={{ paddingTop:10,borderTop:'1px solid var(--nx-divider)',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
            <span style={{ fontSize:10,fontWeight:800,color:'var(--nx-text-muted)',textTransform:'uppercase',letterSpacing:'0.06em' }}>Won Value</span>
            <span style={{ fontSize:15,fontWeight:800,color:D_TONES.success,fontVariantNumeric:'tabular-nums' }}>{fmt(wonValue)}</span>
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════
     SECTION 5 — TEAM LEADERBOARD
     ═══════════════════════════════════════════════════════ */
  const TeamLeaderboard = ({ deals, stages, fmt, onNavigate, loading }) => {
    if (loading) return (
      <div className="nx-panel nx-team-board" style={{ padding:0 }}>
        <div className="nx-panel-head"><Skel h={14} w={140} r={3} /></div>
        <BodySkel rows={5} heights={[32,32,32,32,32]} />
      </div>
    );
    const { wonId, lostId } = getTerminalIds(stages);
    const owners = [...new Set(deals.map(d=>d.owner).filter(Boolean))];
    const board = owners.map(name => {
      const od=deals.filter(d=>d.owner===name);
      const active=od.filter(d=>d.stageId!==wonId&&d.stageId!==lostId);
      const won=od.filter(d=>d.stageId===wonId);
      return { name, pipeVal:active.reduce((s,d)=>s+d.value,0), wonVal:won.reduce((s,d)=>s+d.value,0), winRate:od.length>0?Math.round((won.length/od.length)*100):0 };
    }).sort((a,b)=>b.wonVal-a.wonVal||b.pipeVal-a.pipeVal);
    const maxPipe=Math.max(...board.map(b=>b.pipeVal),1);
    const colors=[D_TONES.primary,D_TONES.success,D_TONES.purple,D_TONES.amber,D_TONES.rose];
    const medals=['🥇','🥈','🥉'];
    return (
      <div className="nx-panel nx-team-board" style={{ padding:0 }}>
        <div className="nx-panel-head">
          <h3 className="nx-panel-title">Team Performance</h3>
          <button className="nx-panel-link" onClick={() => onNavigate('people')}>All Reps <NexusIcon name="ArrowRight" size={11} /></button>
        </div>
        {board.length===0
          ? <div className="nx-empty" style={{ padding:'24px 16px' }}><NexusIcon name="Users" size={20} /><span>No rep data.</span></div>
          : board.map((rep,i) => {
              const col=colors[i%colors.length], init=rep.name.split(' ').map(w=>w[0]).join('').slice(0,2);
              const barPct=Math.max(6,Math.round((rep.pipeVal/maxPipe)*100));
              const rc=rep.winRate>=30?D_TONES.success:rep.winRate>=15?D_TONES.amber:D_TONES.danger;
              return (
                <div key={rep.name} style={{ display:'grid',gridTemplateColumns:'20px 28px 1fr 36px',alignItems:'center',gap:8,padding:'9px 14px',borderBottom:i<board.length-1?'1px solid var(--nx-divider)':'none',cursor:'pointer',transition:'background .1s' }}
                  onMouseEnter={e=>e.currentTarget.style.background='var(--nx-surface-alt)'}
                  onMouseLeave={e=>e.currentTarget.style.background=''}
                >
                  <span style={{ fontSize:i<3?13:10,textAlign:'center',fontWeight:700,color:'var(--nx-text-muted)' }}>{i<3?medals[i]:`#${i+1}`}</span>
                  <span style={{ width:28,height:28,borderRadius:'50%',background:`color-mix(in srgb, ${col} 9%, transparent)`,color:col,display:'grid',placeItems:'center',fontSize:10,fontWeight:800,border:i===0?`1.5px solid ${col}`:'1.5px solid transparent',flexShrink:0 }}>{init}</span>
                  <div style={{ minWidth:0 }}>
                    <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:3 }}>
                      <span style={{ fontSize:12,fontWeight:600,color:'var(--nx-text)' }}>
                        {rep.name.split(' ')[0]}
                        {i===0&&<span style={{ marginLeft:4,fontSize:8,fontWeight:800,color:col,background:`color-mix(in srgb, ${col} 9%, transparent)`,padding:'1px 4px',borderRadius:3 }}>TOP</span>}
                      </span>
                      <span style={{ fontSize:10,color:'var(--nx-text-muted)',fontVariantNumeric:'tabular-nums' }}>{fmt(rep.pipeVal)}</span>
                    </div>
                    <div style={{ height:3,background:'var(--nx-divider)',borderRadius:2,overflow:'hidden' }}>
                      <div style={{ width:barPct+'%',height:'100%',background:col,borderRadius:2,transition:'width .4s' }} />
                    </div>
                  </div>
                  <span style={{ fontSize:12,fontWeight:800,color:rc,textAlign:'right' }}>{rep.winRate}%</span>
                </div>
              );
            })
        }
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════
     SECTION 6 — ACTIVITY INTELLIGENCE (donut + feed)
     ═══════════════════════════════════════════════════════ */
  const ActivityPanel = ({ activity, analyticsData, loading, onRefresh }) => {
    if (loading) return (
      <div className="nx-panel" style={{ gridColumn:'span 3' }}>
        <div className="nx-panel-head"><Skel h={14} w={140} r={3} /></div>
        <div className="nx-panel-body" style={{ display:'flex',gap:14,alignItems:'center' }}>
          <Skel w={68} h={68} r={34} style={{ flexShrink:0 }} />
          <div style={{ flex:1,display:'flex',flexDirection:'column',gap:8 }}><Skel h={11} w="75%" /><Skel h={11} w="60%" /><Skel h={11} w="70%" /><Skel h={11} w="55%" /></div>
        </div>
      </div>
    );
    const counts = useMemo(() => {
      /* Prefer pre-fetched analytics breakdown over in-session activity */
      if (analyticsData && analyticsData.length > 0) {
        const map = { email:{label:'Email',color:D_TONES.purple,icon:'Mail'}, call:{label:'Call',color:D_TONES.success,icon:'Phone'}, meeting:{label:'Meeting',color:D_TONES.amber,icon:'Calendar'}, task:{label:'Task',color:D_TONES.primary,icon:'StickyNote'} };
        return analyticsData.map(item => ({ ...(map[item.type] || {label:item.type,color:'#94a3b8',icon:'Activity'}), count: item.count }));
      }
      const c={email:0,call:0,meeting:0,note:0,deal:0,whatsapp:0};
      activity.forEach(a=>{ if(c[a.type]!==undefined) c[a.type]++; });
      return [
        { label:'Email',   count:c.email,   color:D_TONES.purple, icon:'Mail'      },
        { label:'Call',    count:c.call,    color:D_TONES.success, icon:'Phone'     },
        { label:'Meeting', count:c.meeting, color:D_TONES.amber,  icon:'Calendar'  },
        { label:'Task',    count:c.note,    color:D_TONES.primary, icon:'StickyNote'},
      ];
    }, [activity, analyticsData]);
    const total=counts.reduce((s,x)=>s+x.count,0)||1;
    const R=28, circ=2*Math.PI*R;
    let cum=0;
    const segs=counts.map(t=>{ const len=(t.count/total)*circ, gap=1.5; const s={...t,dashArray:`${Math.max(len-gap,0)} ${circ}`,startLen:cum}; cum+=len; return s; });
    return (
      <div className="nx-panel" style={{ gridColumn:'span 3' }}>
        <div className="nx-panel-head"><h3 className="nx-panel-title">Activity by Type</h3></div>
        <div className="nx-panel-body" style={{ display:'flex', gap:14, alignItems:'center' }}>
          <div style={{ flexShrink:0 }}>
            <svg viewBox="0 0 72 72" width="68" height="68">
              <circle cx="36" cy="36" r={R} fill="none" stroke="var(--nx-divider)" strokeWidth="11" />
              {segs.map(seg=>seg.count>0&&<circle key={seg.label} cx="36" cy="36" r={R} fill="none" stroke={seg.color} strokeWidth="11" strokeDasharray={seg.dashArray} strokeDashoffset={-seg.startLen+circ/4} transform="rotate(-90 36 36)" />)}
              <text x="36" y="33" textAnchor="middle" fontSize="11" fontWeight="800" fill="var(--nx-text)">{total}</text>
              <text x="36" y="42" textAnchor="middle" fontSize="5" fontWeight="600" fill="var(--nx-text-muted)">Total</text>
            </svg>
          </div>
          <div style={{ flex:1, display:'flex', flexDirection:'column', gap:7 }}>
            {counts.map(t=>(
              <div key={t.label} style={{ display:'flex', alignItems:'center', gap:7 }}>
                <span style={{ width:7,height:7,borderRadius:'50%',background:t.color,flexShrink:0 }} />
                <span style={{ flex:1,fontSize:11.5,color:'var(--nx-text)',fontWeight:500 }}>{t.label}</span>
                <span style={{ fontSize:12,fontWeight:700,color:'var(--nx-text)',minWidth:22,textAlign:'right' }}>{t.count}</span>
                <span style={{ fontSize:10,color:'var(--nx-text-muted)',minWidth:32,textAlign:'right' }}>{Math.round(t.count/total*100)}%</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════
     SECTION 7 — PRODUCTIVITY CENTER (tabbed tasks)
     ═══════════════════════════════════════════════════════ */
  const ProductivityCenter = ({ tasks, loading, onNavigate, onComplete }) => {
    const [tab, setTab] = useState('today');
    const today   = new Date().toISOString().split('T')[0];
    const weekEnd = new Date(new Date(today).getTime() + 7*86400000).toISOString().split('T')[0];
    const open    = tasks.filter(t=>!t.done);
    const overdue = tasks.filter(t=>!t.done&&t.dueDate&&t.dueDate<today);
    const tabData = {
      today:   open.filter(t=>t.dueDate===today).slice(0,5),
      week:    open.filter(t=>!t.dueDate||(t.dueDate>=today&&t.dueDate<=weekEnd)).slice(0,8),
      overdue: overdue.slice(0,5),
    };
    const current = tabData[tab]||[];
    return (
      <div className="nx-panel" style={{ gridColumn:'span 3' }}>
        <div className="nx-panel-head">
          <h3 className="nx-panel-title"><NexusIcon name="CheckSquare" size={13} /> Tasks</h3>
          <button className="nx-panel-link" onClick={() => onNavigate('tasks')}>All tasks <NexusIcon name="ArrowRight" size={11} /></button>
        </div>
        <div style={{ display:'flex', borderBottom:'1px solid var(--nx-divider)', padding:'0 12px' }}>
          {[['today','Today'],['week','This Week'],['overdue','Overdue']].map(([id,lbl])=>(
            <button key={id} onClick={()=>setTab(id)} style={{ background:'none',border:'none',padding:'7px 8px',fontSize:11,fontWeight:700,cursor:'pointer',color:tab===id?'var(--nx-accent)':'var(--nx-text-muted)',borderBottom:tab===id?'2px solid var(--nx-accent)':'2px solid transparent',marginBottom:'-1px',transition:'color .1s' }}>
              {lbl}
              {id==='overdue'&&overdue.length>0&&<span style={{ marginLeft:4,background:D_TONES.danger,color:'#fff',fontSize:8,fontWeight:800,padding:'1px 4px',borderRadius:3 }}>{overdue.length}</span>}
            </button>
          ))}
        </div>
        <div className="nx-panel-body nx-panel-body-flush">
          {loading
            ? [0,1,2,3].map(i=><div key={i} className="nx-task-compact" style={{ pointerEvents:'none' }}><Skel w={14} h={14} r={3} style={{ flexShrink:0 }} /><Skel h={11} style={{ flex:1 }} /><Skel w={40} h={16} r={3} style={{ flexShrink:0 }} /></div>)
            : current.length===0
              ? <div className="nx-empty" style={{ padding:'20px 16px' }}><NexusIcon name="CheckSquare" size={18} /><span>All clear!</span></div>
              : current.map(t=>{ const pri=t.priority||'Medium', fg=pri==='High'?D_TONES.danger:pri==='Medium'?D_TONES.amber:D_TONES.primary, bg=pri==='High'?'var(--nx-danger-tint, #FEE2E2)':pri==='Medium'?'var(--nx-warning-tint, #FEF6E6)':'var(--nx-accent-tint, #EDF4FD)'; return (
                  <div key={t.id} className="nx-task-compact"><button className="nx-check" onClick={()=>onComplete&&onComplete(t.id)} /><span className="nx-task-compact-title">{t.title}</span><span className="nx-task-compact-pri" style={{ background:bg,color:fg }}>{pri}</span></div>
                ); })
          }
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════
     SECTION 8 — SALES FUNNEL
     ═══════════════════════════════════════════════════════ */
  const SalesFunnel = ({ deals, stages, fmt, onNavigate, loading }) => {
    if (loading) return (
      <div className="nx-panel" style={{ gridColumn:'span 3' }}>
        <div className="nx-panel-head"><Skel h={14} w={110} r={3} /></div>
        <BodySkel rows={5} heights={[22,4,22,4,22]} />
      </div>
    );
    const { lostId } = getTerminalIds(stages);
    const sd = stages.filter(s=>s.id!==lostId).map(s=>({ ...s, cnt:deals.filter(d=>d.stageId===s.id).length, val:deals.filter(d=>d.stageId===s.id).reduce((sum,d)=>sum+d.value,0) }));
    const maxCnt=Math.max(...sd.map(s=>s.cnt),1);
    if (stages.length === 0) return (
      <div className="nx-panel" style={{ gridColumn:'span 3' }}>
        <div className="nx-panel-head"><h3 className="nx-panel-title">Sales Funnel</h3></div>
        <div className="nx-empty" style={{ padding:'24px 16px' }}><NexusIcon name="Filter" size={18} /><span>No pipeline stages.</span></div>
      </div>
    );
    return (
      <div className="nx-panel" style={{ gridColumn:'span 3' }}>
        <div className="nx-panel-head"><h3 className="nx-panel-title">Sales Funnel</h3></div>
        <div className="nx-panel-body" style={{ display:'flex', flexDirection:'column', gap:6 }}>
          {sd.map((s,i)=>{ const wPct=Math.max(36,(s.cnt/maxCnt)*100), conv=i>0&&sd[i-1].cnt>0?Math.round((s.cnt/sd[i-1].cnt)*100):null; return (
            <div key={s.id}>
              {conv!==null&&<div style={{ display:'flex',alignItems:'center',justifyContent:'center',gap:3,padding:'1px 0' }}><NexusIcon name="ArrowDown" size={9} style={{ color:'var(--nx-text-faint)' }} /><span style={{ fontSize:9,color:'var(--nx-text-faint)',fontWeight:700 }}>{conv}% conversion</span></div>}
              <div style={{ display:'flex',alignItems:'center',gap:6,cursor:'pointer' }} onClick={()=>onNavigate('deals')}>
                <div style={{ flex:1,display:'flex',justifyContent:'center' }}>
                  <div style={{ width:wPct+'%',height:22,background:s.tone,borderRadius:4,display:'flex',alignItems:'center',justifyContent:'center',opacity:0.88,transition:'all .15s' }}
                    onMouseEnter={e=>{e.currentTarget.style.opacity='1';e.currentTarget.style.transform='scaleY(1.06)';}}
                    onMouseLeave={e=>{e.currentTarget.style.opacity='0.88';e.currentTarget.style.transform='';}}
                  ><span style={{ fontSize:10,fontWeight:700,color:'#fff',whiteSpace:'nowrap' }}>{s.name}</span></div>
                </div>
                <span style={{ fontSize:11,fontWeight:700,color:'var(--nx-text)',minWidth:18,textAlign:'right' }}>{s.cnt}</span>
                <span style={{ fontSize:10,color:'var(--nx-text-muted)',minWidth:38,textAlign:'right' }}>{fmt(s.val)}</span>
              </div>
            </div>
          ); })}
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════
     SECTION 9 — FORECAST WIDGET
     ═══════════════════════════════════════════════════════ */
  const ForecastWidget = ({ deals, stages, fmt, loading }) => {
    if (loading) return (
      <div className="nx-panel" style={{ gridColumn:'span 3' }}>
        <div className="nx-panel-head"><Skel h={14} w={100} r={3} /><Skel h={14} w={80} r={3} /></div>
        <BodySkel rows={3} heights={[32,32,32]} />
      </div>
    );
    const { wonId, lostId } = getTerminalIds(stages);
    const stageMap  = useMemo(()=>Object.fromEntries(stages.map(s=>[s.id,s])),[stages]);
    const wonVal    = deals.filter(d=>d.stageId===wonId).reduce((s,d)=>s+d.value,0);
    const openDeals = deals.filter(d=>d.stageId!==wonId&&d.stageId!==lostId);
    const weighted  = openDeals.reduce((s,d)=>{
      const prob = d.probability ?? stageMap[d.stageId]?.prob ?? 50;
      return s + Math.round(d.value*(prob/100));
    }, 0);
    const bestCase  = openDeals.reduce((s,d)=>{
      const prob = Math.max(d.probability ?? stageMap[d.stageId]?.prob ?? 50, 85);
      return s + Math.round(d.value*(prob/100));
    }, 0);
    const worstCase = openDeals.reduce((s,d)=>{
      const prob = Math.min(d.probability ?? stageMap[d.stageId]?.prob ?? 50, 25);
      return s + Math.round(d.value*(prob/100));
    }, 0);
    const rows = [
      { label:'Best Case',  val:wonVal+bestCase,   color:D_TONES.success },
      { label:'Expected',   val:wonVal+weighted,   color:D_TONES.primary },
      { label:'Worst Case', val:wonVal+worstCase,  color:D_TONES.amber   },
    ];
    const estTarget = rows[0].val;
    if (openDeals.length === 0 && wonVal === 0) return (
      <div className="nx-panel" style={{ gridColumn:'span 3' }}>
        <div className="nx-panel-head"><h3 className="nx-panel-title">Forecast</h3></div>
        <div className="nx-empty" style={{ padding:'28px 16px' }}><NexusIcon name="TrendingUp" size={20} /><span>No deals to forecast.</span></div>
      </div>
    );
    return (
      <div className="nx-panel" style={{ gridColumn:'span 3' }}>
        <div className="nx-panel-head">
          <h3 className="nx-panel-title">Forecast</h3>
          <span style={{ fontSize:10,color:'var(--nx-text-muted)',fontWeight:600 }}>Best Case {fmt(estTarget)}</span>
        </div>
        <div className="nx-panel-body" style={{ display:'flex', flexDirection:'column', gap:12 }}>
          {rows.map(r=>{ const pct=estTarget>0?Math.min(Math.round((r.val/estTarget)*100),100):0; return (
            <div key={r.label}>
              <div style={{ display:'flex',justifyContent:'space-between',marginBottom:5 }}>
                <span style={{ fontSize:12,fontWeight:600,color:'var(--nx-text)' }}>{r.label}</span>
                <span style={{ fontSize:12,fontWeight:800,color:r.color,fontVariantNumeric:'tabular-nums' }}>{fmt(r.val)}</span>
              </div>
              <div style={{ height:5,background:'var(--nx-divider)',borderRadius:3,overflow:'hidden' }}>
                <div style={{ width:pct+'%',height:'100%',background:r.color,borderRadius:3,transition:'width .4s' }} />
              </div>
              <div style={{ display:'flex',justifyContent:'flex-end',marginTop:3 }}>
                <span style={{ fontSize:9.5,color:'var(--nx-text-muted)',fontWeight:600 }}>{estTarget>0?pct+'% of target':'—'}</span>
              </div>
            </div>
          ); })}
          <div style={{ paddingTop:10,borderTop:'1px solid var(--nx-divider)',display:'flex',justifyContent:'space-between',alignItems:'center' }}>
            <span style={{ fontSize:10,fontWeight:700,color:'var(--nx-text-muted)',textTransform:'uppercase',letterSpacing:'0.06em' }}>Won This Period</span>
            <span style={{ fontSize:15,fontWeight:800,color:D_TONES.success,fontVariantNumeric:'tabular-nums' }}>{fmt(wonVal)}</span>
          </div>
        </div>
      </div>
    );
  };

  /* ═══════════════════════════════════════════════════════
     MAIN DASHBOARD VIEW
     ═══════════════════════════════════════════════════════ */
  const DashboardView = ({ onNavigate, onOpenRecord }) => {
    const store        = useStore();
    const stages       = (store.pipeline && store.pipeline.length > 0) ? store.pipeline : [];
    const loading      = store.loading;
    const toast        = useToast();
    const brandName    = store.branding?.brandName || 'Dashboard';
    const OwnerAv      = window.OwnerAvatar;
    const FilterChip   = window.FilterChip;
    const ModuleHeader = window.ModuleHeader;
    const [rangeId,        setRangeId]        = useState('30');
    const [customStart,    setCustomStart]    = useState('');
    const [customEnd,      setCustomEnd]      = useState('');
    const [ownerFilter,    setOwnerFilter]    = useState(null);
    const [activityCounts, setActivityCounts] = useState(null);

    // D-03: re-fetch Activity By Type whenever range or owner changes
    useEffect(() => {
      const wsId = store.workspaceId;
      if (!wsId || !window.__nx_api?.analytics?.getActivityFeed) return;
      const { from, to } = getRangeBounds(rangeId, customStart, customEnd);
      const params = {};
      if (from) params.rangeStart = from.toISOString();
      if (to)   params.rangeEnd   = to.toISOString();
      const ownerMember = ownerFilter
        ? (store.workspaceMembers || []).find(m => m.name === ownerFilter)
        : null;
      if (ownerMember?.userId) params.ownerId = ownerMember.userId;
      window.__nx_api.analytics.getActivityFeed(wsId, params)
        .then(res => { if (Array.isArray(res) && res.length > 0) setActivityCounts(res); })
        .catch(() => {});
    }, [store.workspaceId, rangeId, customStart, customEnd, ownerFilter, store.workspaceMembers]);

    const range    = DASH_RANGES.find(r => r.id === rangeId) || DASH_RANGES[3]; // Last 30 Days default
    const dealsAll = store.deals;
    const { wonId, lostId } = useMemo(() => getTerminalIds(stages), [stages]);
    const owners   = useMemo(() => [...new Set(dealsAll.map(d => d.owner).filter(Boolean))], [dealsAll]);

    // Compute bounds once; shared by all deal filters below
    const { from: cutoff, to: cutoffEnd } = useMemo(
      () => getRangeBounds(rangeId, customStart, customEnd),
      [rangeId, customStart, customEnd]
    );

    // D-01/D-02: active deals filtered by createdAt; won/lost filtered by closeDate
    const activeDeals = useMemo(() => dealsAll.filter(d =>
      d.stageId !== wonId && d.stageId !== lostId &&
      (!ownerFilter || d.owner === ownerFilter) &&
      inRange(d.createdAt, cutoff, cutoffEnd)
    ), [dealsAll, ownerFilter, wonId, lostId, cutoff, cutoffEnd]);

    const wonDeals = useMemo(() => dealsAll.filter(d =>
      d.stageId === wonId &&
      (!ownerFilter || d.owner === ownerFilter) &&
      inRange(d.closeDate, cutoff, cutoffEnd)
    ), [dealsAll, ownerFilter, wonId, cutoff, cutoffEnd]);

    const lostDeals = useMemo(() => dealsAll.filter(d =>
      d.stageId === lostId &&
      (!ownerFilter || d.owner === ownerFilter) &&
      inRange(d.closeDate, cutoff, cutoffEnd)
    ), [dealsAll, ownerFilter, lostId, cutoff, cutoffEnd]);

    // Unified deals for widgets that need all segments (ForecastWidget, TeamLeaderboard)
    const deals = useMemo(() => [...activeDeals, ...wonDeals, ...lostDeals],
      [activeDeals, wonDeals, lostDeals]);

    const tasksOpen   = store.tasks.filter(t => !t.done && (!ownerFilter || (t.assignee || t.owner) === ownerFilter));
    const wonValue    = wonDeals.reduce((s, d) => s + d.value, 0);
    const openValue   = activeDeals.reduce((s, d) => s + d.value, 0);
    const closedTotal = wonDeals.length + lostDeals.length;
    const winRate     = closedTotal > 0 ? Math.round((wonDeals.length / closedTotal) * 100) : 0;
    const avgDeal     = activeDeals.length > 0 ? Math.round(activeDeals.reduce((s, d) => s + d.value, 0) / activeDeals.length) : 0;
    const fmt = n => '$' + (n>=1e6?(n/1e6).toFixed(1)+'M':n>=1e3?(n/1e3).toFixed(0)+'K':Math.round(n));

    // D-04: sparklines bound to current dashboard range
    const kpiSeries = useMemo(() => ({
      pipeline: buildTrendPts(dealsAll, RANGE_TO_TREND[rangeId] || '30D', 'value', wonId, lostId).series,
      opps:     buildTrendPts(dealsAll, RANGE_TO_TREND[rangeId] || '30D', 'count', wonId, lostId).series,
    }), [dealsAll, wonId, lostId, rangeId]);

    // D-05: contacts — headline = total; subtitle = new in selected period
    const newContacts = useMemo(() =>
      store.people.filter(p => inRange(p.createdAt, cutoff, cutoffEnd)),
      [store.people, cutoff, cutoffEnd]
    );
    const rangeDisplayLabel = useMemo(() => {
      if (rangeId === 'custom' && customStart && customEnd) {
        const MO = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
        const fmt = s => { const [,m,d] = s.split('-').map(Number); return `${MO[m-1]} ${d}`; };
        return `${fmt(customStart)}–${fmt(customEnd)}`;
      }
      return range.label;
    }, [rangeId, customStart, customEnd, range.label]);

    const kpis = [
      { id:'pipeline', label:'Pipeline Value',     value:loading?'…':fmt(openValue),              sub:'Open opportunities',           icon:'Target',     color:D_TONES.primary, go:'deals',  series:kpiSeries.pipeline },
      { id:'opps',     label:'Open Opportunities', value:loading?'…':String(activeDeals.length),  sub:'Active deals in pipeline',     icon:'TrendingUp', color:D_TONES.success, go:'deals',  series:kpiSeries.opps     },
      { id:'won',      label:'Won This Period',     value:loading?'…':fmt(wonValue),               sub:wonDeals.length+' deals closed (by close date)', icon:'Award', color:D_TONES.purple, go:'deals' },
      { id:'winrate',  label:'Win Rate',            value:loading?'…':winRate+'%',                 sub:closedTotal+' closed deals total',                icon:'PieChart', color:winRate>=40?D_TONES.success:winRate>=20?D_TONES.amber:D_TONES.danger, go:'deals' },
      { id:'contacts', label:'Total Contacts',      value:loading?'…':String(store.people.length), trend:newContacts.length>0?'+'+newContacts.length+' new':null, up:true, sub:newContacts.length+' added '+rangeDisplayLabel.toLowerCase(), icon:'Users', color:D_TONES.rose, go:'people' },
      { id:'avgdeal',  label:'Avg Deal Size',       value:loading?'…':fmt(avgDeal),                sub:'Active pipeline average',      icon:'DollarSign', color:D_TONES.amber, go:'deals'  },
    ];

    const buildExportRows = () => [[brandName+' — Dashboard Export'],['Generated',new Date().toLocaleString()],['Range',rangeDisplayLabel],[],['KPI','Value'],...kpis.map(k=>[k.label,k.value]),[],['Stage','Count','Value'],...stages.filter(s=>s.id!=='s6').map(s=>{ const sd=deals.filter(d=>d.stageId===s.id); return [s.name,sd.length,sd.reduce((sum,d)=>sum+d.value,0)]; })];
    const exportCSV = () => { const rows=buildExportRows(), csv=rows.map(r=>r.map(c=>{const s=String(c==null?'':c);return s.includes(',')||s.includes('"')?'"'+s.replace(/"/g,'""')+'"':s;}).join(',')).join('\n'), a=document.createElement('a'); a.href=URL.createObjectURL(new Blob([csv],{type:'text/csv;charset=utf-8'})); a.download=brandName.toLowerCase().replace(/\s+/g,'-')+'-dashboard-'+range.id+'-'+new Date().toISOString().slice(0,10)+'.csv'; a.click(); toast({tone:'success',icon:'Download',title:'CSV Exported'}); };
    const exportPDF = () => { const rows=buildExportRows(), esc=s=>String(s==null?'':s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;'), win=window.open('','_blank','width=900,height=720'); if(!win){toast({tone:'error',icon:'AlertCircle',title:'Pop-up blocked'});return;} win.document.write('<html><head><title>'+brandName+' Dashboard</title><style>body{font-family:"Nunito Sans",sans-serif;padding:28px 36px;color:#0F2A4A}table{width:100%;border-collapse:collapse;font-size:12px}td{padding:6px 10px;border-bottom:1px solid #EEF2F7}</style></head><body><h2>'+brandName+' Dashboard</h2><table>'+rows.map(r=>'<tr>'+r.map(c=>'<td>'+esc(c)+'</td>').join('')+'</tr>').join('')+'</table><scr'+'ipt>window.onload=()=>setTimeout(()=>window.print(),300)</sc'+'ript></body></html>'); win.document.close(); toast({tone:'success',icon:'Printer',title:'PDF Print Dialog Opened'}); };
    const exportExcel = () => { const rows=buildExportRows(), tsv=rows.map(r=>r.map(c=>String(c==null?'':c).replace(/\t/g,' ')).join('\t')).join('\r\n'), a=document.createElement('a'); a.href=URL.createObjectURL(new Blob(['\uFEFF'+tsv],{type:'text/tab-separated-values;charset=utf-8'})); a.download=brandName.toLowerCase().replace(/\s+/g,'-')+'-dashboard-'+range.id+'-'+new Date().toISOString().slice(0,10)+'.xls'; a.click(); toast({tone:'success',icon:'FileSpreadsheet',title:'Excel Exported'}); };

    return (
      <div className="nx-view nx-home">
        <ModuleHeader title={`Welcome back, ${window.__nx_auth?.user?.name || 'there'}`} actions={<>
          <span className="nx-dash-date-chip"><NexusIcon name="Calendar" size={11} />{new Date().toLocaleDateString('en-US',{weekday:'short',month:'short',day:'numeric',year:'numeric'})}</span>
          <span className="nx-dash-actions-sep" />
          <FilterChip icon="Calendar" label="Range" value={rangeDisplayLabel} options={DASH_RANGES.map(r=>r.label)} active
            onChange={label => { const r = DASH_RANGES.find(x => x.label === label); if (r) { setRangeId(r.id); if (r.id !== 'custom') { setCustomStart(''); setCustomEnd(''); } } }} />
          {rangeId === 'custom' && (
            <span style={{ display:'inline-flex', alignItems:'center', gap:3, padding:'0 8px', border:'1px dashed var(--nx-accent)', borderRadius:999, height:26, fontSize:11, background:'var(--nx-surface)', flexShrink:0 }}>
              <input type="date" value={customStart} max={customEnd||''} onChange={e => setCustomStart(e.target.value)}
                style={{ border:'none', background:'none', fontSize:11, fontFamily:'inherit', color:'var(--nx-text)', outline:'none', width:100, cursor:'pointer' }} />
              <span style={{ color:'var(--nx-text-muted)', lineHeight:1 }}>–</span>
              <input type="date" value={customEnd} min={customStart||''} onChange={e => setCustomEnd(e.target.value)}
                style={{ border:'none', background:'none', fontSize:11, fontFamily:'inherit', color:'var(--nx-text)', outline:'none', width:100, cursor:'pointer' }} />
            </span>
          )}
          <FilterChip icon="User" label="Rep" value={ownerFilter||'All Owners'} options={['All Owners',...owners]} active={!!ownerFilter} onChange={v=>setOwnerFilter(v==='All Owners'?null:v)} />
          <span className="nx-dash-actions-sep" />
          <ExportDropdown onCSV={exportCSV} onExcel={exportExcel} onPDF={exportPDF} />
          <span className="nx-dash-actions-sep" />
          <IconBtn icon="RefreshCw" size={13} title="Refresh dashboard" onClick={() => store.reloadWorkspace()} />
        </>} />

        <div className="nx-home-grid">

          {/* ── Row 1: 6 compact KPI cards ── */}
          <div className="nx-kpi-compact-row">
            {kpis.map(k => (
              <SmartKpiCard key={k.id} loading={loading}
                label={k.label} value={k.value} trend={k.trend} up={k.up} sub={k.sub} icon={k.icon} color={k.color}
                series={k.series||null}
                onClick={() => k.go&&onNavigate(k.go)} />
            ))}
          </div>

          {/* ── Row 2: Pipeline(4) + Trend(5) + Win/Loss(3) — always rendered; widgets show skeleton while loading ── */}
          <StageBarChart deals={deals} stages={stages} fmt={fmt} onNavigate={onNavigate} loading={loading} />
          <PipelineTrend deals={deals} allDeals={ownerFilter ? dealsAll.filter(d=>d.owner===ownerFilter) : dealsAll} stages={stages} fmt={fmt} onNavigate={onNavigate} loading={loading} />
          <WinLossPanel wonDeals={wonDeals} lostDeals={lostDeals} activeDeals={activeDeals} winRate={winRate} wonValue={wonValue} fmt={fmt} loading={loading} />

          {/* ── Row 3: Team(3) + Top Deals(full width) + Activity Feed(3) ── */}
          <TeamLeaderboard deals={deals} stages={stages} fmt={fmt} onNavigate={onNavigate} loading={loading} />

          <div className="nx-panel nx-home-deals">
            <div className="nx-panel-head">
              <h3 className="nx-panel-title"><NexusIcon name="Target" size={13} /> Top Deals to Close</h3>
              <button className="nx-panel-link" onClick={() => onNavigate('deals')}>View Pipeline <NexusIcon name="ArrowRight" size={11} /></button>
            </div>
            <div className="nx-panel-body nx-panel-body-flush">
              {loading
                ? <div style={{ padding:'12px 16px',display:'flex',flexDirection:'column',gap:10 }}>{[0,1,2,3].map(i=><div key={i} style={{ display:'grid',gridTemplateColumns:'2fr 1fr 1fr 1fr 1fr 1fr 1fr 36px',gap:8,alignItems:'center' }}>{[180,100,80,80,90,90,60,24].map((w,j)=><Skel key={j} h={13} w={w} />)}</div>)}</div>
                : activeDeals.length===0
                  ? <div className="nx-empty" style={{ padding:'36px 16px',flexDirection:'column',gap:12 }}><NexusIcon name="Target" size={28} /><span>No active deals in this period.</span><Btn variant="secondary" icon="Plus" onClick={()=>onNavigate('deals')}>Open Pipeline</Btn></div>
                  : <div className="nx-table-wrap"><div className="nx-table-scroll"><table className="nx-table">
                    <thead><tr>
                      <th><span className="nx-th-inner"><span>Deal</span></span></th>
                      <th><span className="nx-th-inner"><span>Account</span></span></th>
                      <th><span className="nx-th-inner"><span>Stage</span></span></th>
                      <th><span className="nx-th-inner"><span>Value</span></span></th>
                      <th><span className="nx-th-inner"><span>Close Date</span></span></th>
                      <th><span className="nx-th-inner"><span>Probability</span></span></th>
                      <th><span className="nx-th-inner"><span>Owner</span></span></th>
                      <th className="nx-th-end" />
                    </tr></thead>
                    <tbody>
                      {activeDeals.sort((a,b)=>b.value-a.value).slice(0,6).map(d=>(
                        <DealRow key={d.id} deal={d} stages={stages} companies={store.companies} fmt={fmt}
                          Pill={Pill} NexusIcon={NexusIcon} IconBtn={IconBtn} OwnerAv={OwnerAv} toast={toast} onOpenRecord={onOpenRecord} />
                      ))}
                    </tbody>
                  </table></div></div>
              }
            </div>
          </div>

          <div className="nx-panel" style={{ gridColumn:'span 3' }}>
            <div className="nx-panel-head">
              <h3 className="nx-panel-title"><NexusIcon name="Activity" size={13} /> Recent Activity</h3>
              <span style={{ fontSize:10, color:'var(--nx-text-faint)', fontWeight:500 }}>Latest events · not period-filtered</span>
            </div>
            <div className="nx-panel-body nx-panel-body-flush nx-panel-body-bounded">
              {loading
                ? [0,1,2,3,4].map(i=><div key={i} className="nx-tl-row" style={{ pointerEvents:'none' }}><Skel w={24} h={24} r={6} style={{ flexShrink:0 }} /><div style={{ flex:1,display:'flex',flexDirection:'column',gap:5 }}><Skel h={11} w="75%" /><Skel h={9} w="55%" /></div><Skel w={28} h={9} /></div>)
                : store.activity.length===0 && store.notes.length===0
                  ? <div className="nx-empty" style={{ padding:'24px 16px' }}><NexusIcon name="Activity" size={20} /><span>No recent activity.</span></div>
                  : (store.activity.length > 0 ? store.activity : store.notes.slice(0,7)).map(a=>{ const icMap={whatsapp:'MessageCircle',deal:'Target',email:'Mail',note:'StickyNote',call:'Phone'}; const icName=icMap[a.type]||'Activity'; const fgMap={whatsapp:D_TONES.success,deal:D_TONES.primary,email:D_TONES.purple,call:D_TONES.amber}; const icFg=fgMap[a.type]||'var(--nx-text-muted)'; const bgMap={whatsapp:'var(--nx-success-tint, #E7F5EE)',deal:'var(--nx-accent-tint, #EDF4FD)',email:'var(--nx-purple-tint, #ede9fe)',call:'var(--nx-warning-tint, #FEF6E6)'}; const icBg=bgMap[a.type]||'var(--nx-surface-alt)'; return (
                      <div key={a.id} className="nx-tl-row" title={a.detail||''}>
                        <span className="nx-tl-icon" style={{ background:icBg,color:icFg }}><NexusIcon name={icName} size={11} /></span>
                        <div className="nx-tl-body"><span className="nx-tl-primary"><strong>{a.who}</strong> {a.what}</span>{a.detail&&<span className="nx-tl-secondary">{a.detail}</span>}</div>
                        <span className="nx-tl-time">{a.time}</span>
                      </div>
                    ); })
              }
            </div>
          </div>

          {/* ── Row 4: Productivity(3) + Funnel(3) + ActivityByType(3) + Forecast(3) ── */}
          <ProductivityCenter tasks={tasksOpen} loading={loading} onNavigate={onNavigate} onComplete={id=>store.update('tasks',id,{done:true}).catch(()=>toast({tone:'error',icon:'AlertCircle',title:'Failed to complete task'}))} />
          <SalesFunnel deals={deals} stages={stages} fmt={fmt} onNavigate={onNavigate} loading={loading} />
          <ActivityPanel activity={store.activity.length > 0 ? store.activity : store.notes.slice(0,7)} analyticsData={activityCounts} loading={loading} />
          <ForecastWidget deals={deals} stages={stages} fmt={fmt} loading={loading} />

        </div>
      </div>
    );
  };

  window.DashboardView = DashboardView;
})();
